<template>
  <div class="wrapper" data-step="dashboard">
    <Header v-if="!isUserPending" :name="$t('app.home.welcome', { name: user.personal_information.first_name })" />
    <transition name="fade" mode="out-in">
      <div class="stats-wrapper">
        <div class="cards-container">
          <StatsCard
            v-if="!isActivatedParticipationsPending"
            icon="wallet"
            :label="$t('app.home.activatedEvaluations', { count: activatedParticipations?.current_month || 0 })"
            :stats="activatedParticipations || null"
          />
          <StatsCard
            v-if="!isEvaluatedParticipationsPending"
            icon="target"
            :label="$t('app.home.evaluatedTalents', { count: evaluatedParticipations?.current_month || 0 })"
            :stats="evaluatedParticipations || null"
          />
          <div class="card credit-card">
            <div v-if="!isCreditsPending" class="credits">
              <span class="credit-number" v-text="credits.available" />
              <span
                class="credit-text"
                v-text="$t('app.home.creditsLeft', { count: credits.available })"
              />
            </div>
          </div>
        </div>
        <div class="cards-container">
          <div class="card">
            <div class="card-header">
              <div class="card-title" v-text="$t('app.home.lastResultsTitle')" />
            </div>
            <div v-if="!isLastEvaluatedParticipationsPending && lastEvaluatedParticipations.length" class="last-results-container">
              <CompatibilityCard v-for="(result, index) in lastEvaluatedParticipations.splice(0, 8)" :participant="result.participant" :campaign="result.campaign" :key="index"></CompatibilityCard>
            </div>
            <Empty v-else :message="$t('app.home.noResults')" :dark="isDark" icon="folder" />
          </div>
        </div>
        <div class="cards-container duo-cards">
          <div class="card">
            <div class="card-header">
              <div class="card-title" v-text="$t('app.home.completionRateTitle')" />
            </div>
            <div class="completion-stats-container">
              <Doughnut
                :data="doughnutData"
                :displayTotal="true"
                :cutout="80"
                :totalLabel="$t('app.home.talentsLabel', { count: doughnutData.datasets[0].data.reduce((a, b) => a + b, 0) })"
              />
            </div>
          </div>
          <div class="card">
            <div class="card-header">
              <div class="card-title" v-text="$t('app.home.newTalentsTitle')" />
            </div>
            <Table v-if="!isActivableParticipationsPending && activableParticipations.length" :data="activableParticipations" :fields="state.fields" class="table-light">
              <template #[`participant.candidate.last_name`]="{ data }">
                <span class="participant-name" v-if="data.participant.candidate_id" v-safe-html="`${data.participant.candidate.first_name} ${data.participant.candidate.last_name}`" />
                <span class="participant-name" v-else v-safe-html="data.participant.pending_first_name && data.participant.pending_last_name ? `${data.participant.pending_first_name} ${data.participant.pending_last_name}` : data.participant.pending_email" />
              </template>
              <template #actions="actionsProps">
                <Button
                  :label="$t('app.home.activateEvaluation')"
                  class="primary icon icon-left sm"
                  icon="bi-play-btn"
                  @click="state.showActionModal = true; state.participant = actionsProps.row"
                />
              </template>
            </Table>
            <Empty v-else :message="$t('app.home.noTalentsToActivate')" :dark="isDark" icon="box" />
          </div>
        </div>
      </div>
    </transition>
    <ActionModal modal="activate" :data="[state.participant]" :isOpen="state.showActionModal" @close="closeActionModal" @confirm="actionModalConfirm" />
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useDark } from "@vueuse/core";

import CompatibilityCard from "@/components/campaigns/CompatibilityCard.vue";
import ActionModal from "@/components/modals/app/actionModal/ActionModal.vue";
import Header from "@/components/Header.vue";
import Doughnut from "@/components/charts/Doughnut.vue";
import Empty from "@/components/table/Empty.vue";

import { useAppStore } from "@/stores/app.js";

import { useRoles, useUser } from "@/queries/user.query";
import {
  useActivableParticipationsStats,
  useActivatedParticipationsStats,
  useLastEvaluationsStats,
  useEvaluationCountStats,
  useParticipationsStateStats,
  useOrganisationCredits,
  useOrganisation
} from "@/queries/organisation.query";

import { usePermissions } from "@/composables/permissions";
import { spaceApi } from "@/api/space.api";
import { MemberRole } from "@/types/role";

const isDark = useDark();
const router = useRouter();
const { t } = useI18n();

// Init stores
const appStore = useAppStore();

const { data: roles, isPending: isRolesPending } = useRoles();
const { canWriteBillings } = usePermissions();
const { data: organisation, isPending: isOrganisationPending } = useOrganisation();
const { data: user, isPending: isUserPending } = useUser();
const { data: credits, isPending: isCreditsPending } = useOrganisationCredits();

const { data: activableParticipations, isPending: isActivableParticipationsPending } = useActivableParticipationsStats();
const { data: activatedParticipations, isPending: isActivatedParticipationsPending } = useActivatedParticipationsStats();
const { data: evaluatedParticipations, isPending: isEvaluatedParticipationsPending } = useEvaluationCountStats();
const { data: lastEvaluatedParticipations, isPending: isLastEvaluatedParticipationsPending } = useLastEvaluationsStats();
const { data: participationsState, isPending: isParticipationsStatePending } = useParticipationsStateStats();

const state = reactive({
  showActionModal: false,
  participant: null,
  activateModalIsLoading: false,
  fields: [
    {
      id: "participant.candidate.last_name",
      fallback: "participant.pending_last_name",
      label: t("app.home.talentField"),
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "campaign.name",
      label: t("app.home.campaignField"),
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
  ],
});

const doughnutData = computed(() => {
  return {
    labels: [t("app.home.completedEvaluations"), t("app.home.ongoingEvaluations"), t("app.home.notStartedEvaluations")],
    datasets: [
      {
        backgroundColor: ["#F2CB36", "#D9901A", isDark.value ? "#FFFFFF" : "#1D1D1D"],
        data: !isParticipationsStatePending.value ? Object.values(participationsState.value) : [0],
      },
    ],
  };
});

function closeActionModal() {
  state.showActionModal = false;
  state.participant = null;
}

async function actionModalConfirm() {
  closeActionModal();
}

watch(roles, async () => {
  if (roles.value && roles.value.length > 0) {
    const spaces = await spaceApi.getAll(appStore.currentOrganisationId);
    if (roles.value[0].role_backward_compatibility_id === MemberRole.CONSULTANT && spaces?.length > 0) {
      router.push({
        name: 'campaign-selector',
        params: {
          companyId: appStore.currentOrganisationId,
          spaceId: spaces[0].workspace_id,
        }
      });
    }
  }
})
</script>
